import { useCallback, useEffect, useState } from 'react';

export type InternalNetworkTunnelUiStatus = {
  connected: boolean;
  reconnecting?: boolean;
  sessionId?: string;
  tenantId?: string;
  internalAccessNetworkId?: string;
  preferredInternalAccessNetworkId?: string;
  networkSlug?: string;
  expiresAt?: string;
  httpProxyPort?: number;
  socksPort?: number;
  routeTorchThroughSession?: boolean;
  error?: string;
};

export const useInternalNetworkTunnel = () => {
  const [status, setStatus] = useState<InternalNetworkTunnelUiStatus>({
    connected: false,
  });
  const [isPending, setIsPending] = useState(false);

  const refreshStatus = useCallback(async () => {
    const api = window.internalNetworkTunnelAPI;

    if (!api) {
      return;
    }

    setStatus(await api.status());
  }, []);

  useEffect(() => {
    const api = window.internalNetworkTunnelAPI;
    const unsubscribe = api?.onStatus((next) => {
      setStatus(next);
    });

    void refreshStatus();

    return unsubscribe;
  }, [refreshStatus]);

  const start = useCallback(async (internalAccessNetworkId?: string) => {
    const api = window.internalNetworkTunnelAPI;

    if (!api) {
      setStatus((previous) => ({
        ...previous,
        connected: false,
        error: 'Internal network tunnel access is only available in Torch',
      }));

      return;
    }

    setIsPending(true);

    try {
      const next = await api.start(
        internalAccessNetworkId ? { internalAccessNetworkId } : undefined,
      );

      setStatus(next);
    } catch (error: unknown) {
      setStatus((previous) => ({
        ...previous,
        connected: false,
        reconnecting: false,
        error:
          error instanceof Error
            ? error.message
            : 'Could not start the internal network tunnel',
      }));
    } finally {
      setIsPending(false);
    }
  }, []);

  const stop = useCallback(async () => {
    const api = window.internalNetworkTunnelAPI;

    setIsPending(true);

    try {
      const next = api ? await api.stop() : { connected: false };

      setStatus(next);
    } catch (error: unknown) {
      setStatus((previous) => ({
        ...previous,
        connected: false,
        reconnecting: false,
        error:
          error instanceof Error
            ? error.message
            : 'Could not stop the internal network tunnel',
      }));
    } finally {
      setIsPending(false);
    }
  }, []);

  const setRouteTorchThroughSession = useCallback(async (enabled: boolean) => {
    const api = window.internalNetworkTunnelAPI;

    if (!api) {
      return;
    }

    setStatus(await api.setRouteTorchThroughSession(enabled));
  }, []);

  const setPreferredInternalAccessNetwork = useCallback(
    async (internalAccessNetworkId: string) => {
      const api = window.internalNetworkTunnelAPI;

      if (!api?.setPreferredInternalAccessNetwork) {
        return;
      }

      setStatus(
        await api.setPreferredInternalAccessNetwork(internalAccessNetworkId),
      );
    },
    [],
  );

  return {
    status,
    isPending,
    start,
    stop,
    setRouteTorchThroughSession,
    setPreferredInternalAccessNetwork,
  };
};

export type InternalNetworkTunnelControls = ReturnType<
  typeof useInternalNetworkTunnel
>;
